import { useState, useEffect, useCallback, useMemo } from "react";
import * as z from "zod";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { ChevronLeft } from "lucide-react";
import { CampaignForm } from "@/components/corporate/campaign/CampaignForm";
import { useDebounce } from "@/hooks/use-debounce";
import { createCampaign, fetchOpportunities } from "@/lib/api/campaign";

// API Functions

// Campaign form schema for step 1
const campaignInfoSchema = z.object({
  name: z.string().min(1, "Campaign name is required"),
  meta: z.object({
    description: z.string().min(1, "Description is required"),
    theme: z.string().min(1, "Theme is required"),
    goals: z.array(z.string().min(1, "Goal cannot be empty")).min(1, "At least one goal is required"),
  }),
  active_from: z.date(),
  active_to: z.date(),
  is_active: z.boolean().default(true),
});

// Event details schema for step 3
const eventDetailsSchema = z.object({
  locations: z.array(z.object({
    country: z.string().min(1, "Country is required"),
    state: z.string().min(1, "State is required"),
    district: z.string().min(1, "District is required"),
    volunteers: z.number().min(1, "Number of volunteers is required"),
    details: z.string().optional(),
  })).min(1, "At least one location is required"),
  additional_details: z.string().optional(),
});

// Combined schema for all steps
const campaignFormSchema = z.object({
  // Step 1: Campaign Info
  name: z.string().min(1, "Campaign name is required"),
  meta: z.object({
    description: z.string().min(1, "Description is required"),
    theme: z.string().min(1, "Theme is required"),
    goals: z.array(z.string().min(1, "Goal cannot be empty")).min(1, "At least one goal is required"),
  }),
  active_from: z.date(),
  active_to: z.date(),
  is_active: z.boolean().default(true),
  
  // Step 2: SKUs/Events
  opportunities: z.array(z.string()).min(1, "At least one opportunity is required"),
  
  // Step 3: Event Details
  locations: z.array(z.object({
    country: z.string().min(1, "Country is required"),
    state: z.string().min(1, "State is required"),
    district: z.string().min(1, "District is required"),
    volunteers: z.number().min(1, "Number of volunteers is required"),
    details: z.string().optional(),
  })).min(1, "At least one location is required"),
  additional_details: z.string().optional(),
});

type CampaignFormValues = z.infer<typeof campaignFormSchema>;

const CorporateNewCampaignsPage = ({ session, profile }: { session: any; profile: any }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  
  // State for opportunity search
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  // Extract query parameters for prefilling
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      name: params.get('name') || '',
      description: params.get('description') || '',
      theme: params.get('theme') || '',
      startDate: params.get('startDate') ? new Date(params.get('startDate')!) : undefined,
      endDate: params.get('endDate') ? new Date(params.get('endDate')!) : undefined,
      opportunityIds: params.getAll('opportunityIds[]'),
      goals: params.getAll('goals[]')
    };
  }, [location.search]);

  // Fetch opportunities with React Query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingOpportunities
  } = useInfiniteQuery({
    queryKey: ['opportunities', debouncedSearchQuery],
    queryFn: ({ pageParam }) => {
      return fetchOpportunities({
        pageParam,
        token: session?.access_token,
        searchQuery: debouncedSearchQuery,
        limit: 6
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.pagination) {
        return undefined;
      }
      
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!session?.access_token
  });
  
  const opportunities = data?.pages.flatMap(page => 
    Array.isArray(page.data) ? page.data : (page.data ? [page.data] : [])
  ) || [];
  
  // Create campaign mutation
  const { mutate, isPending } = useMutation({
    mutationFn: (campaignData: any) => createCampaign(session?.access_token, campaignData),
    onSuccess: (data) => {
      toast({
        title: "Campaign created",
        description: "Your campaign has been created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      navigate(`/dashboard/campaigns`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  // Handle form submission
  const handleSubmit = (
    data: CampaignFormValues, 
    goals: string[], 
    locations: Location[], 
    selectedOpportunities: string[]
  ) => {
    // Prepare campaign data for submission
    const campaignData = {
      name: data.name,
      active_from: data.active_from,
      active_to: data.active_to,
      is_active: data.is_active,
      meta: {
        ...data.meta,
        goals,
        additional_details: data.additional_details,
        locations: locations.map(location => ({
          country: location.country,
          state: location.state,
          district: location.district,
          meta: {
            volunteers: location.volunteers,
            details: location.details
          }
        })),
        opportunities: selectedOpportunities
      }
    };
    
    // Submit the campaign data
    mutate(campaignData);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/campaigns')}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
          <h1 className="text-2xl font-bold">Create New Campaign</h1>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>New Campaign</CardTitle>
          <CardDescription>
            Create a new campaign by filling out the information below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CampaignForm
            initialData={{
              name: queryParams.name,
              meta: {
                description: queryParams.description,
                theme: queryParams.theme,
              },
              active_from: queryParams.startDate || new Date(),
              active_to: queryParams.endDate || new Date(new Date().setDate(new Date().getDate() + 30)),
              is_active: true,
              additional_details: "",
            }}
            initialGoals={queryParams.goals.length > 0 ? queryParams.goals : undefined}
            initialSelectedOpportunities={queryParams.opportunityIds}
            opportunities={opportunities}
            isLoadingOpportunities={isLoadingOpportunities}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            onSubmit={handleSubmit}
            submitButtonText="Create Campaign"
            isSubmitting={isPending}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CorporateNewCampaignsPage;
